import { useCallback, useEffect, useState } from "react";
import { App, Card, Space, Table, Tag, Typography } from "antd";
import { Link } from "react-router-dom";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import type { SubscriberRow, SubscribersResponse } from "../../types/notifications";
import { SEND_INTERVAL_LABELS } from "../../types/filterAdmin";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";

const { Title, Paragraph, Text } = Typography;

/** Kto dostaje co: pracownik × filtr × częstotliwość z ustawień w Supabase. */
export const NotificationSubscriptionsHealth = () => {
  const { notification } = App.useApp();
  const [rows, setRows] = useState<SubscriberRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminFetch<SubscribersResponse>(
        `notifications/subscriptions?limit=2000${activeOnly ? "&active_only=true" : ""}`,
      );
      setRows(response.data ?? []);
    } catch (error) {
      notification.error({ message: "Nie udało się pobrać odbiorców", description: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [activeOnly, notification]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Card>
      <Title level={4}>Odbiorcy powiadomień</Title>
      <Paragraph type="secondary">
        Pracownicy z częstotliwościami ustawionymi na filtrach (<Text code>UserFilterPreferences</Text>) — już po
        sprawdzeniu, że filtr jest dla nich widoczny i mają uprawnienie. Kanały (e-mail/SMS/aplikacja) są ustawieniem
        osoby per typ powiadomienia.
      </Paragraph>
      <Space style={{ marginBottom: 16 }}>
        <ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />
      </Space>
      <Table<SubscriberRow>
        rowKey={(row) => `${row.employee_id}:${row.filter_id}:${row.interval}`}
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 50 }}
        scroll={{ x: true }}
        columns={[
          {
            title: "Filtr",
            key: "filter",
            render: (_: unknown, row) => <Link to={`/filters/edit/${row.filter_id}`}>{row.filter_name ?? row.filter_id}</Link>,
          },
          {
            title: "Częstotliwość",
            dataIndex: "interval",
            key: "interval",
            render: (interval: string) => (
              <Tag color={interval === "instantly" ? "purple" : "green"}>{SEND_INTERVAL_LABELS[interval]?.split(" (")[0] ?? interval}</Tag>
            ),
          },
          {
            title: "Pracownik",
            dataIndex: "employee_id",
            key: "employee_id",
            render: (id: string, row) => (
              <Text copyable={{ text: id }}>
                {id}
                {row.filter_owner_id === id ? <Tag style={{ marginLeft: 8 }}>autor filtra</Tag> : null}
              </Text>
            ),
          },
          { title: "SMS", dataIndex: "can_sms", key: "can_sms", render: (v: boolean) => (v ? "uprawniony" : "—") },
        ]}
      />
    </Card>
  );
};
